import { Box, Card, Grid, Skeleton, Typography } from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import HubIcon from '@mui/icons-material/Hub';
import CustomSnackbar from 'components/CustomSnackbar';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import LiveClock from 'components/LiveClock/LiveClock.js';
import MDTypography from 'components/MDTypography/index';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchControllers } from '../../stores/sensor/controllersSlice';
import { setSearchString } from '../../stores/sensor/sensorStore';
import { ControllerCard } from './ControllerCard';
import DevicesCount from 'components/DevicesCount/DevicesCount.js';
import { filterDevices } from '../../helpers/filterHelpers';

const REFRESH_TIME_IN_SECONDS = 300;
const SKELETON_CARDS_COUNT = 8;

const shouldShowSourceSensorsNotice = controller => {
  const sourceSensorIds = Array.isArray(controller.source_sensors_ids) ? controller.source_sensors_ids : [];

  if (sourceSensorIds.length > 1) return true;
  if (sourceSensorIds.length !== 1) return false;

  return Number(sourceSensorIds[0]) !== Number(controller.id);
};

export const ControllersList = () => {
  const controllers = useSelector(state => state.controllers.controllers);
  const currentUser = useSelector(state => state.sensors.currentUser);
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const { distributor, customer, building, floor, area, hvacEquipment, searchString } = selectedFilterObject;
  const controllerSelectedStatus = useSelector(state => state.controllers.selectedStatus);
  const isLoading = useSelector(state => state.controllers.loading);
  const schedulesIds = useSelector(s => s.controllers.schedulesIds || []);
  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal } = snackbarState;
  const dispatch = useDispatch();

  const renderControllerSkeletons = () =>
    Array.from({ length: SKELETON_CARDS_COUNT }).map((_, index) => (
      <Grid
        container
        sx={{
          lineHeight: 0,
          position: 'relative',
          marginBottom: '1rem',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'flex-start',
          alignSelf: 'flex-start',
          height: 'fit-content',
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'start' },
          boxSizing: 'border-box',
          px: { xs: 0.5, sm: 0 },
        }}
        xs={12}
        md={6}
        lg={4}
        xl={3}
        key={`controller-skeleton-${index}`}>
        <Card
          sx={{
            position: 'relative',
            overflow: 'visible',
            borderRadius: 4,
            width: { xs: '100%', sm: 'fit-content' },
          }}>
          <Skeleton
            variant="circular"
            sx={{
              position: 'absolute',
              top: { xs: 6, sm: 12 },
              right: { xs: 4, sm: 12 },
              width: { xs: 18, sm: 30 },
              height: { xs: 18, sm: 30 },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ml: { xs: 0.5, sm: 1 },
              mr: { xs: 0.2, sm: 1.25 },
              my: -0.5,
              padding: { xs: '8px 0px 0px 0px', sm: '12px 0px 4px 0px' },
            }}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  width: { xs: '100%', sm: '19.5rem' },
                  alignItems: 'flex-start',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: { xs: '30px', sm: '48px' } }}>
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: { xs: '1.7rem', sm: '3rem' },
                      height: { xs: '1.7rem', sm: '3rem' },
                      borderRadius: 2.5,
                      flexShrink: 0,
                    }}
                  />
                  <Skeleton variant="text" height={26} sx={{ ml: { xs: 0.75, sm: 1 }, flex: 1 }} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: '.5rem', width: { xs: '100%', sm: '19rem' }, pb: '.3rem' }}>
                <Skeleton variant="rounded" width={14} height={14} sx={{ ml: 0.2, mr: 0.5, flexShrink: 0 }} />
                <Skeleton variant="text" height={18} width="62%" />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', p: { xs: '6px', sm: '.5rem' }, gap: { xs: 0.5, sm: '.5rem' }, alignItems: 'center' }}>
            {[0, 1, 2].map(tile => (
              <Skeleton
                key={tile}
                variant="rounded"
                sx={{ width: '33.333%', height: { xs: '3rem', sm: '4rem' }, borderRadius: 2.5 }}
              />
            ))}
          </Box>
        </Card>
      </Grid>
    ));

  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  // Local filtering - excludes unassigned controllers
  const localFilteredControllers = useMemo(() => {
    if (!currentUser.role || controllers?.length === 0) return [];

    // Apply centralized filter
    const filtered = filterDevices(
      controllers || [],
      {
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        status: controllerSelectedStatus,
        searchString,
      },
      currentUser,
    );

    // Filter out unassigned controllers (building_id is null)
    // const assignedOnly = filtered.filter(controller => controller.building_id !== null);

    // Sort by status (Active → Inactive)
    return filtered.sort((a, b) => {
      if (a.status !== b.status) return a.status - b.status;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [controllers, distributor, customer, building, floor, area, hvacEquipment, controllerSelectedStatus, searchString, currentUser]);

  const getDisplayTitle = () => {
    if (building !== 'all') {
      const buildingName = localFilteredControllers?.find(d => d.building_id?.toString() === building)?.building;
      return `${buildingName || 'Building'} controllers list`;
    }
    return 'All controllers list';
  };

  useEffect(() => {
    dispatch(fetchControllers());
    dispatch(setSearchString(''));

    const interval = setInterval(() => {
      dispatch(fetchControllers());
    }, REFRESH_TIME_IN_SECONDS * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  function formatDayDdMonthYear(date, timeZone) {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      ...(timeZone && { timeZone }),
    });
    const parts = Object.fromEntries(fmt.formatToParts(date).map(p => [p.type, p.value]));
    return `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}`;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100dvh - 150px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box ml={1}>
          <MDTypography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold' }} color="dark">
            {getDisplayTitle()}
          </MDTypography>
          <CustomSnackbar
            open={snackbarState.snackbarOpen}
            duration={3000}
            onClose={handleSnackbarClose}
            message="Please select a distributor and a customer"
            vertical={vertical}
            horizontal={horizontal}
          />

          {<DevicesCount filteredDevices={localFilteredControllers || []} devicesType="controller"></DevicesCount>}
        </Box>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <LiveClock />
          <Typography sx={{ fontSize: '.85rem' }}>{formatDayDdMonthYear(new Date())}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
        <Box ml={0} mb={1} sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <DevicesListFilter type="controllers" hvacEquipment />
          <Grid
            className="controllersListContainer"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              justifyContent: 'flex-start',
              flex: 1,
              minHeight: 'calc(100dvh - 280px)',
              overflowY: 'visible',
              overflowX: 'visible',
              width: { xs: '100%', sm: '100%' },
              flexWrap: 'wrap',
              px: { xs: 0.5, sm: 0 },
              pb: '14rem',
            }}
            mt={2}>
            {isLoading ? (
              renderControllerSkeletons()
            ) : localFilteredControllers?.length === 0 ? (
              <Card
                role="status"
                sx={{
                  width: '100%',
                  maxWidth: 520,
                  mx: 'auto',
                  mt: { xs: 3, sm: 5 },
                  p: { xs: 2.5, sm: 3 },
                  border: '1px solid',
                  borderColor: 'info.light',
                  borderRadius: 4,
                  boxShadow: '0 12px 28px rgba(30, 136, 229, 0.10)',
                  background: 'linear-gradient(135deg, rgba(227, 242, 253, 0.9), rgba(255, 255, 255, 0.96))',
                  textAlign: 'center',
                }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 1.5,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'info.main',
                    backgroundColor: 'rgba(33, 150, 243, 0.12)',
                  }}>
                  <FilterAltOffOutlinedIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant="h6" color="text.primary" fontWeight={700} gutterBottom>
                  No matching controllers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto', lineHeight: 1.6 }}>
                  Try adjusting the filters or search term to see more controllers.
                </Typography>
              </Card>
            ) : (
              localFilteredControllers?.map(controller => (
                <Grid
                  container
                  sx={{
                    lineHeight: 0,
                    position: 'relative',
                    marginBottom: '1rem',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'flex-start',
                    alignSelf: 'flex-start',
                    height: 'fit-content',
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'start' },
                    boxSizing: 'border-box',
                    px: { xs: 0.5, sm: 0 },
                  }}
                  xs={12}
                  md={6}
                  lg={4}
                  xl={3}
                  key={controller.id}>
                  <ControllerCard
                    className="controllerCard"
                    id={controller.id}
                    name={controller.name}
                    deviceType={controller.device_type}
                    controller_model={controller.controller_model}
                    comfort={controller.comfort}
                    building_id={controller.building_id}
                    building={controller.building_name}
                    floorNumber={controller.floor_number}
                    area_name={controller.area_name}
                    noticeIcon={shouldShowSourceSensorsNotice(controller) ? <HubIcon fontSize="small" /> : null}
                    noticeTooltip="This controller uses source sensors for its decision logic"
                    params={{
                      last_record_time: controller.latest_controller_data?.last_record_time,
                      on_off: controller.latest_controller_data?.on_off,
                      control: controller.latest_controller_data?.control,
                      mode: controller.latest_controller_data?.mode,
                      cool_water_valve: controller.latest_controller_data?.cool_water_valve,
                      occupancy: controller.latest_controller_data?.occupancy,
                      keypad_lock: controller.latest_controller_data?.keypad_lock,
                      channel_1: controller.latest_controller_data?.channel_1,
                      channel_2: controller.latest_controller_data?.channel_2,
                    }}
                    displayData={true}
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
